import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Check, Copy, Loader2, RefreshCw } from 'lucide-react';

import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { SettingsPanel, SettingsFieldGrid } from '@/components/settings/SettingsConsole';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import meetingsService from '@/services/meetings.service';
import whatsappService, { type WhatsappSettings } from '@/services/whatsapp.service';

/**
 * Einstellungen → WhatsApp.
 *
 * Der Kunde verbindet seine eigene WhatsApp-Business-Nummer (Meta Cloud API).
 * Eingehende Nachrichten werden automatisch zu Tickets; Antworten aus dem
 * Ticket gehen über WhatsApp zurück — inklusive 24-Stunden-Fenster-Schutz.
 */
export default function WhatsappSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Kanal bağlantıları ORG bazlıdır — her shop kendi hesabını bağlar.
  const orgsQuery = useQuery({
    queryKey: ['voice-orgs'],
    queryFn: () => meetingsService.organizations(),
    staleTime: 5 * 60_000,
  });
  const orgs = orgsQuery.data || [];
  const [orgId, setOrgId] = useState('');
  useEffect(() => {
    if (!orgId && orgs.length) setOrgId(orgs[0].id);
  }, [orgs, orgId]);
  const oid = orgId || undefined;

  const { data, isLoading } = useQuery<WhatsappSettings>({
    queryKey: ['whatsapp-settings', orgId],
    queryFn: () => whatsappService.getSettings(oid),
    enabled: Boolean(orgId) || (orgsQuery.isFetched && orgs.length === 0),
  });

  const [accessToken, setAccessToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!data) return;
    setPhoneNumberId(data.config.phone_number_id || '');
    setWabaId(data.config.waba_id || '');
    setIsActive(Boolean(data.config.is_active));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      whatsappService.saveSettings({
        ...(accessToken.trim() ? { access_token: accessToken.trim() } : {}),
        ...(appSecret.trim() ? { app_secret: appSecret.trim() } : {}),
        phone_number_id: phoneNumberId.trim() || null,
        waba_id: wabaId.trim() || null,
        is_active: isActive,
      }, oid),
    onSuccess: (res) => {
      queryClient.setQueryData(['whatsapp-settings', orgId], res);
      setAccessToken('');
      setAppSecret('');
      toast.success(t('settings:whatsapp.saved', 'WhatsApp-Einstellungen gespeichert'));
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const testMutation = useMutation({
    mutationFn: () => whatsappService.testSettings(oid),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-settings'] });
      if (r.success) {
        toast.success(
          t('settings:whatsapp.testOk', 'Verbunden: {{name}} ({{number}})', {
            name: r.verifiedName || 'WhatsApp Business',
            number: r.displayNumber || '—',
          }),
        );
      } else {
        toast.error(r.error || t('settings:whatsapp.testFailed', 'Verbindung fehlgeschlagen'));
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const copy = (value: string, label: string) => {
    void navigator.clipboard.writeText(value);
    toast.success(t('settings:whatsapp.copied', '{{what}} kopiert', { what: label }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const connected = Boolean(data?.connected);

  return (
    <div className="space-y-4">
      {/* --- Organisation wählen (nur bei Mitgliedschaft in mehreren) ------ */}
      {orgs.length > 1 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Building2 className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">
              {t('settings:voice.org.title', 'Organisation')}
            </p>
            <p className="text-xs text-slate-500">
              {t('settings:voice.org.hint', 'Nummer, KI und Einstellungen gelten je Organisation.')}
            </p>
          </div>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-9 w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}


      <SettingsPanel
        title={t('settings:whatsapp.connection.title', 'WhatsApp Business verbinden')}
        description={t(
          'settings:whatsapp.connection.description',
          'Meta Cloud API: App im Meta-Entwicklerportal anlegen, WhatsApp hinzufügen, dauerhaften Token erzeugen — die IDs stehen unter WhatsApp → API-Setup.',
        )}
        action={
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                <Check className="mr-1 h-3 w-3" />
                {data?.config.display_number || t('settings:whatsapp.state.connected', 'Verbunden')}
              </Badge>
            ) : (
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                {t('settings:whatsapp.state.notConnected', 'Nicht verbunden')}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => testMutation.mutate()}
              disabled={!connected || testMutation.isPending}
            >
              {testMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
              )}
              {t('settings:whatsapp.test', 'Verbindung testen')}
            </Button>
          </div>
        }
      >
        <SettingsFieldGrid>
          <div className="space-y-1.5">
            <Label htmlFor="wa-token">{t('settings:whatsapp.accessToken', 'Access Token (permanent)')}</Label>
            <Input
              id="wa-token"
              type="password"
              autoComplete="off"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={data?.config.access_token ? '••••••••  (unverändert)' : 'EAAG…'}
            />
            <p className="text-xs text-slate-500">
              {t('settings:whatsapp.accessTokenHint', 'System-User-Token mit whatsapp_business_messaging. Leer lassen = gespeicherten behalten.')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wa-phone-id">{t('settings:whatsapp.phoneNumberId', 'Phone Number ID')}</Label>
            <Input
              id="wa-phone-id"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="123456789012345"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wa-waba">{t('settings:whatsapp.wabaId', 'WhatsApp Business Account ID (optional)')}</Label>
            <Input
              id="wa-waba"
              value={wabaId}
              onChange={(e) => setWabaId(e.target.value)}
              placeholder="987654321098765"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wa-secret">{t('settings:whatsapp.appSecret', 'App Secret (optional, empfohlen)')}</Label>
            <Input
              id="wa-secret"
              type="password"
              autoComplete="off"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={data?.config.app_secret ? '••••••••  (unverändert)' : ''}
            />
            <p className="text-xs text-slate-500">
              {t('settings:whatsapp.appSecretHint', 'Aktiviert die Signaturprüfung eingehender Webhooks.')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>{t('settings:whatsapp.activation', 'WhatsApp aktiv')}</Label>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
              <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!connected && !accessToken.trim()} />
              <span className="text-sm text-slate-600">
                {isActive
                  ? t('settings:whatsapp.activeOn', 'Nachrichten werden zu Tickets, Antworten gehen über WhatsApp')
                  : t('settings:whatsapp.activeOff', 'WhatsApp ist deaktiviert')}
              </span>
            </div>
          </div>
        </SettingsFieldGrid>

        <div className="mt-4 flex justify-end">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common:action.save', 'Speichern')}
          </Button>
        </div>
      </SettingsPanel>

      {/* Webhook-Daten — erst nach dem ersten Speichern vorhanden */}
      {data?.config.verify_token && (
        <SettingsPanel
          title={t('settings:whatsapp.webhook.title', 'Webhook bei Meta eintragen')}
          description={t(
            'settings:whatsapp.webhook.description',
            'Meta-App → WhatsApp → Konfiguration → Webhook: beide Werte eintragen und das Feld „messages" abonnieren.',
          )}
        >
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t('settings:whatsapp.webhookUrl', 'Callback-URL')}</Label>
              <div className="flex gap-2">
                <Input readOnly value={data.webhookUrl} className="font-mono text-xs" />
                <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(data.webhookUrl, 'Callback-URL')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('settings:whatsapp.verifyToken', 'Verify-Token')}</Label>
              <div className="flex gap-2">
                <Input readOnly value={data.config.verify_token} className="font-mono text-xs" />
                <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(data.config.verify_token!, 'Verify-Token')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="flex items-start gap-2 text-xs text-slate-500">
              <WhatsAppIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              {t(
                'settings:whatsapp.windowHint',
                'Hinweis: Antworten sind nur innerhalb von 24 Stunden nach der letzten Kundennachricht möglich (WhatsApp-Regel). Danach muss der Kunde zuerst wieder schreiben.',
              )}
            </p>
          </div>
        </SettingsPanel>
      )}
    </div>
  );
}
